import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Camera, Calendar, Clock, User, Mail, Phone, MapPin, CheckCircle, ArrowRight } from 'lucide-react';
import './Booking.css';

const Booking = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const [selectedService, setSelectedService] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    location: '',
    message: ''
  });

  const services = [
    {
      id: 'portraits',
      title: 'Portrait Photography',
      description: 'Professional portraits for individuals, families, and special occasions',
      price: 'Starting at $199',
      icon: Camera
    },
    {
      id: 'headshots',
      title: 'Company Headshots',
      description: 'Professional headshots for business and corporate use',
      price: 'Starting at $149',
      icon: User
    },
    {
      id: 'real-estate',
      title: 'Real Estate Photography',
      description: 'High-quality property photography for listings and marketing',
      price: 'Starting at $199',
      icon: MapPin
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Booking submitted:', { selectedService, formData });
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="booking-page">
      {/* Hero Section */}
      <section className="hero-section" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/BookNowHeader.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Book Your Session
            </motion.h1>
            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Ready to capture your special moments? Book a professional photography session 
              and let us create beautiful memories that last a lifetime.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Service Selection */}
      <section className="service-selection">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Choose Your Service</h2>
            <p>Select the photography service that best fits your needs</p>
          </motion.div>

          <motion.div 
            className="services-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {services.map((service) => (
              <motion.div 
                key={service.id}
                className={`service-card ${selectedService === service.id ? 'selected' : ''}`}
                variants={fadeInUp}
                onClick={() => setSelectedService(service.id)}
              >
                <div className="service-icon">
                  <service.icon size={40} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <div className="service-price">{service.price}</div>
                {selectedService === service.id && (
                  <div className="selected-indicator">
                    <CheckCircle size={20} />
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="booking-form-section">
        <div className="container">
          <div className="booking-layout">
            <motion.div 
              className="form-container"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2>Book Your Session</h2>
              <form onSubmit={handleSubmit} className="booking-form">
                {/* Form inputs commented out - to be handled later */}
                {/* <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="date">Preferred Date *</label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="time">Preferred Time *</label>
                    <select
                      id="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select time</option>
                      <option value="09:00">9:00 AM</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="13:00">1:00 PM</option>
                      <option value="14:00">2:00 PM</option>
                      <option value="15:00">3:00 PM</option>
                      <option value="16:00">4:00 PM</option>
                      <option value="17:00">5:00 PM</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Studio, outdoor location, or address"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Additional Details</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Tell us about your vision, special requirements, or any questions..."
                  />
                </div>

                <button type="submit" className="submit-btn">
                  Book Your Session
                  <ArrowRight size={20} />
                </button> */}
              </form>
            </motion.div>

            <motion.div 
              className="contact-sidebar"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="sidebar-content">
                <h3>Contact Information</h3>
                <div className="contact-info">
                  <div className="contact-item">
                    <Mail size={20} />
                    <div>
                      <strong>Email</strong>
                      <a href="mailto:vellichorvisions@gmail.com">vellichorvisions@gmail.com</a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <Phone size={20} />
                    <div>
                      <strong>Phone</strong>
                      <a href="tel:+14803343330">(480) 334-3330</a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <MapPin size={20} />
                    <div>
                      <strong>Location</strong>
                      <span>Scottsdale & Phoenix, AZ</span>
                    </div>
                  </div>
                </div>

                <div className="booking-info">
                  <h4>What to Expect</h4>
                  <ul>
                    <li>Professional consultation</li>
                    <li>Flexible scheduling</li>
                    <li>High-quality equipment</li>
                    <li>Quick turnaround time</li>
                    <li>Digital delivery</li>
                  </ul>
                </div>

                <div className="cta-card">
                  <h4>Need Help?</h4>
                  <p>Have questions about our services or pricing? We're here to help!</p>
                  <Link to="/contact" className="contact-link">
                    Contact Us
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Frequently Asked Questions</h2>
            <p>Common questions about our booking process and services</p>
          </motion.div>

          <motion.div 
            className="faq-grid"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <motion.div className="faq-item" variants={fadeInUp}>
              <h3>How far in advance should I book?</h3>
              <p>We recommend booking at least 2-3 weeks in advance to ensure availability. For special events or weekends, we suggest booking 4-6 weeks ahead.</p>
            </motion.div>

            <motion.div className="faq-item" variants={fadeInUp}>
              <h3>What if I need to reschedule?</h3>
              <p>We understand that life happens! You can reschedule your session up to 48 hours before your scheduled time at no additional cost.</p>
            </motion.div>

            <motion.div className="faq-item" variants={fadeInUp}>
              <h3>How long does a typical session last?</h3>
              <p>Portrait sessions typically last 1-2 hours, headshots take about 30-45 minutes, and real estate photography varies based on property size.</p>
            </motion.div>

            <motion.div className="faq-item" variants={fadeInUp}>
              <h3>When will I receive my photos?</h3>
              <p>We provide a quick turnaround time of 3-5 business days for standard editing. Rush delivery is available for an additional fee.</p>
            </motion.div>

            <motion.div className="faq-item" variants={fadeInUp}>
              <h3>Do you travel for sessions?</h3>
              <p>Yes! We serve the greater Scottsdale and Phoenix areas. Travel fees may apply for locations outside our standard service area.</p>
            </motion.div>

            <motion.div className="faq-item" variants={fadeInUp}>
              <h3>What payment methods do you accept?</h3>
              <p>We accept all major credit cards, PayPal, and bank transfers. A 50% deposit is required to secure your booking.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Booking; 